import React, { useState, useEffect, useRef } from 'react';
import { supabase, parseTimeToSeconds, getTopSolvers, getCurrentDate } from './supabaseClient';
import html2canvas from 'html2canvas';
import Footer from './Footer';

function ResultScreen({ navigateTo, goBack, isLoggedIn, currentUser, userProfile, solveData: propsData }) {
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [animateRank, setAnimateRank] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [topFive, setTopFive] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Notification modal state
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyResultsLocked, setNotifyResultsLocked] = useState(false);
  const [notifyNewPuzzle, setNotifyNewPuzzle] = useState(false);
  const [notifyDaily, setNotifyDaily] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [notificationSaveSuccess, setNotificationSaveSuccess] = useState(false);
  
  // Share functionality state
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareRankData, setShareRankData] = useState(null);
  const shareCardRef = useRef(null);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use actual solveData from props, or fallback to mock data for testing
  const solveData = propsData || {
    time: '6:23',
    difficulty: 'hard',
    mode: 'standard',
    mistakes: 1,
    isEligible: true,
    timestamp: Date.now()
  };

  // Mock rank data (will be replaced with real API data later)
  const rankData = {
    estimatedPercentile: 85,
    actualRank: 47,
    totalSolvers: 892
  };

  const [solvedDifficulties] = useState({
    easy: false,
    hard: true,
    master: false
  });

  const solvedCount = Object.values(solvedDifficulties).filter(Boolean).length;
  const allSolved = solvedCount === 3;

  // Fetch real leaderboard data
  useEffect(() => {
    fetchTopSolvers();
  }, [solveData.difficulty]);

  const fetchTopSolvers = async () => {
    setIsLoadingLeaderboard(true);
    try {
      const today = getCurrentDate();
      const solvers = await getTopSolvers(solveData.difficulty, today, 5);
      setTopFive(solvers);
    } catch (error) {
      console.error('Error fetching top solvers:', error);
      setTopFive([]);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  // Capitalize difficulty name
  const difficultyName = solveData.difficulty 
    ? solveData.difficulty.charAt(0).toUpperCase() + solveData.difficulty.slice(1)
    : 'Hard';

  const isStandardMode = solveData.mode === 'standard';

  // Auto-save solve to database when logged in
  useEffect(() => {
    if (isLoggedIn && currentUser && solveData && !isSaving) {
      saveSolveToDatabase();
    }
  }, [isLoggedIn, currentUser]);

  const saveSolveToDatabase = async () => {
    if (!solveData || isSaving) return;
    
    setIsSaving(true);
    
    try {
      const timeSeconds = parseTimeToSeconds(solveData.time);
      
      const { data, error } = await supabase
        .from('solves')
        .insert({
          user_id: currentUser.id,
          difficulty: solveData.difficulty,
          mode: solveData.mode,
          time_seconds: timeSeconds,
          mistakes: solveData.mistakes,
          is_eligible: solveData.isEligible,
          puzzle_date: new Date().toISOString().split('T')[0]
        });

      if (error) {
        // Check if it's a duplicate (already solved this difficulty today)
        if (error.code === '23505') {
          console.log('Already solved this difficulty today');
        } else {
          console.error('Error saving solve:', error);
          setSaveFailed(true);
        }
      } else {
        console.log('Solve saved successfully');
      }
    } catch (error) {
      console.error('Error saving solve:', error);
      setSaveFailed(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateAccount = () => {
    navigateTo('login', { fromPuzzle: true, solveData: solveData });
  };

  const handleLogIn = () => {
    navigateTo('login', { fromPuzzle: true, solveData: solveData });
  };

  const handleSaveNotifications = async () => {
    if (!isLoggedIn || !currentUser) {
      alert('Please log in to save notification preferences');
      return;
    }

    setIsSavingNotifications(true);
    setNotificationSaveSuccess(false);

    try {
      const preferences = [];

      // Add final_result preference if enabled
      if (notifyResultsLocked) {
        preferences.push({
          user_id: currentUser.id,
          type: 'final_result',
          is_enabled: true,
          difficulty: solveData.difficulty, // Use the difficulty they just solved
          updated_at: new Date().toISOString()
        });
      }

      // Add next_puzzle preference if enabled
      if (notifyNewPuzzle) {
        preferences.push({
          user_id: currentUser.id,
          type: 'next_puzzle',
          is_enabled: true,
          difficulty: 'all',
          updated_at: new Date().toISOString()
        });
      }

      // Add daily_reminder preference if enabled
      if (notifyDaily) {
        preferences.push({
          user_id: currentUser.id,
          type: 'daily_reminder',
          is_enabled: true,
          difficulty: 'all',
          send_hour_utc: 12, // Default to noon UTC
          updated_at: new Date().toISOString()
        });
      }

      // Save all preferences
      if (preferences.length > 0) {
        const { error } = await supabase
          .from('email_preferences')
          .upsert(preferences, {
            onConflict: 'user_id,type,difficulty'
          });

        if (error) {
          console.error('Error saving notifications:', error);
          alert('Failed to save preferences. Please try again.');
          setIsSavingNotifications(false);
          return;
        }
      }

      // Show success state
      setNotificationSaveSuccess(true);
      
      // Close modal after 1.5 seconds
      setTimeout(() => {
        setShowNotifyModal(false);
        setNotificationSaveSuccess(false);
        // Reset toggles
        setNotifyResultsLocked(false);
        setNotifyNewPuzzle(false);
        setNotifyDaily(false);
      }, 1500);

    } catch (error) {
      console.error('Error saving notifications:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const getPrimaryCTAs = () => {
    // Always show two buttons: "Solve Another" and "Notify Me"
    return [
      {
        text: 'Solve Another',
        action: () => navigateTo('difficulty'),
        isPrimary: true
      },
      {
        text: 'Notify Me',
        action: () => setShowNotifyModal(true),
        isPrimary: false
      }
    ];
  };

  const primaryCTAs = getPrimaryCTAs();

  const getPercentileText = () => {
    const percentile = rankData.estimatedPercentile;
    if (percentile >= 80) {
      return `Around the top ${100 - percentile}% of today's solves`;
    }
    return `Faster than ~${percentile}% of today's solvers`;
  };

  const handleShareResult = async () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      alert('Please login or register to share your results');
      return;
    }

    setIsGeneratingShare(true);
    setShareError(null);

    try {
      // Fetch real rank data for standard mode
      let computedRank = null;
      if (isStandardMode) {
        const today = getCurrentDate();
        
        console.log('Fetching rank data for:', {
          date: today,
          difficulty: solveData.difficulty,
          userId: currentUser.id
        });
        
        // Fetch all eligible solves for this difficulty today
        const { data: todaySolves, error } = await supabase
          .from('solves')
          .select('user_id, time_seconds, solved_at')
          .eq('puzzle_date', today)
          .eq('difficulty', solveData.difficulty)
          .eq('is_eligible', true)
          .order('time_seconds', { ascending: true })
          .order('solved_at', { ascending: true });

        console.log('Fetched solves:', todaySolves);

        if (error) {
          console.error('Error fetching rank data:', error);
        } else if (todaySolves && todaySolves.length > 0) {
          // Find current user's position
          const userIndex = todaySolves.findIndex(s => s.user_id === currentUser.id);
          console.log('User index in results:', userIndex);
          
          if (userIndex !== -1) {
            const currentRank = userIndex + 1; // 1-indexed
            const totalSolves = todaySolves.length;
            const percentile = totalSolves >= 10 ? Math.round(((totalSolves - currentRank) / totalSolves) * 100) : null;
            
            computedRank = {
              currentRank,
              totalSolves,
              percentile
            };
            
            console.log('Computed rank:', computedRank);
          } else {
            console.warn('User not found in today\'s solves');
          }
        } else {
          console.warn('No eligible solves found for today');
        }
      }

      // Store computed rank data for the share card
      setShareRankData(computedRank);
      console.log('shareRankData set to:', computedRank);

      // Wait a moment for the card to render with new data
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!shareCardRef.current) {
        throw new Error('Share card not found');
      }

      // Capture the share card
      const canvas = await html2canvas(shareCardRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        width: 1080,
        height: 1350
      });

      // Convert to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          throw new Error('Failed to generate image');
        }

        const fileName = `sudokumatch-${solveData.difficulty}-${Date.now()}.png`;

        // Try native share on mobile
        if (navigator.share && navigator.canShare) {
          try {
            const file = new File([blob], fileName, { type: 'image/png' });
            await navigator.share({
              files: [file],
              title: 'My SudokuMatch Result',
              text: `I solved today's ${solveData.difficulty} puzzle in ${solveData.time}!`
            });
          } catch (shareError) {
            // User cancelled or share failed, fallback to download
            if (shareError.name !== 'AbortError') {
              downloadImage(blob, fileName);
            }
          }
        } else {
          // Desktop or no share API - download
          downloadImage(blob, fileName);
        }

        setIsGeneratingShare(false);
        setShareRankData(null); // Clear after capture
      }, 'image/png');

    } catch (error) {
      console.error('Error generating share image:', error);
      setShareError('Failed to generate image. Please try again.');
      setIsGeneratingShare(false);
      setShareRankData(null);
    }
  };

  const downloadImage = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {showRegistrationSuccess && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'white',
            padding: '32px 48px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937' }}>
              Nice — your result is now official.
            </div>
          </div>
        </div>
      )}

      {/* Main content wrapper - centered */}
      <div style={{
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
      <div style={{
        maxWidth: '600px',
        width: '100%'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px 20px' : '40px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          marginBottom: '20px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '32px' }}>
            <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: isMobile ? '8px' : '12px' }}>🎉</div>
            <h1 style={{
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}>
              Puzzle Complete!
            </h1>
            <div style={{
              fontSize: '16px',
              color: '#6B7280',
              fontWeight: '500'
            }}>
              {difficultyName} • {isStandardMode ? 'Standard Mode' : 'Relaxed Mode'}
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            marginBottom: isMobile ? '20px' : '32px',
            padding: isMobile ? '16px' : '24px',
            background: '#F9FAFB',
            borderRadius: '12px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Your Time
            </div>
            <div style={{
              fontSize: isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1F2937',
              fontFamily: 'monospace',
              marginBottom: isMobile ? '8px' : '12px'
            }}>
              {solveData.time === '00:00' && !isStandardMode ? solveData.actualTime || solveData.time : solveData.time}
            </div>
            
            {/* Only show rank/percentile for Standard mode */}
            {isStandardMode ? (
              <div style={{
                fontSize: '16px',
                color: '#4B5563',
                fontWeight: '500',
                opacity: animateRank ? 0 : 1,
                transition: 'opacity 0.4s ease'
              }}>
                {isLoggedIn ? (
                  <>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#2563EB',
                      marginBottom: '4px',
                      opacity: animateRank ? 1 : 0,
                      transform: animateRank ? 'scale(1)' : 'scale(0.8)',
                      transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}>
                      Ranked #{rankData.actualRank} of {rankData.totalSolvers} today
                    </div>
                    <div style={{ fontSize: '15px', color: '#6B7280' }}>
                      Faster than {Math.round((1 - rankData.actualRank / rankData.totalSolvers) * 100)}% of players
                    </div>
                  </>
                ) : (
                  getPercentileText()
                )}
              </div>
            ) : (
              <div style={{
                fontSize: '14px',
                color: '#6B7280',
                fontWeight: '500',
                fontStyle: 'italic'
              }}>
                Relaxed mode — not ranked
              </div>
            )}
          </div>

          {/* Ineligibility warning - only for Standard mode */}
          {isStandardMode && !solveData.isEligible && (
            <div style={{
              marginBottom: isMobile ? '16px' : '24px',
              padding: '12px 16px',
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#92400E',
              textAlign: 'center'
            }}>
              Not eligible for leaderboard (3+ mistakes in Standard mode)
            </div>
          )}

          {/* Top solvers section - only for Standard mode */}
          {isStandardMode && (
            <div style={{
              marginBottom: isMobile ? '16px' : (isLoggedIn ? '24px' : '32px'),
              position: 'relative'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Today's Top Solvers
              </h3>
              
              <div style={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  filter: !isLoggedIn ? 'blur(1px)' : 'none',
                  opacity: !isLoggedIn ? 0.6 : 1,
                  transition: 'all 0.5s ease'
                }}>
                  {isLoadingLeaderboard ? (
                    <div style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#6B7280',
                      fontSize: '14px'
                    }}>
                      Loading leaderboard...
                    </div>
                  ) : topFive.length > 0 ? (
                    topFive.map((player, index) => (
                      <div
                        key={player.userId || index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          background: player.rank <= 3 ? 'rgba(255, 140, 0, 0.05)' : 'white',
                          borderBottom: index === topFive.length - 1 ? 'none' : '1px solid #E5E7EB'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            width: '28px',
                            height: '28px',
                            background: player.rank <= 3 ? '#FF8C00' : '#E5E7EB',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: player.rank <= 3 ? 'white' : '#6B7280'
                          }}>
                            {player.rank}
                          </span>
                          <span style={{ 
                            fontWeight: '600', 
                            color: '#1F2937',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {player.username}
                            {player.founderTier === 'paid' && (
                              <span style={{ fontSize: '14px' }}>👑</span>
                            )}
                            {player.founderTier === 'free' && (
                              <span style={{ fontSize: '14px' }}>⭐</span>
                            )}
                          </span>
                        </div>
                        <span style={{
                          fontWeight: '700',
                          fontSize: '16px',
                          fontFamily: 'monospace',
                          color: '#1F2937'
                        }}>
                          {player.time}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '40px 16px',
                      textAlign: 'center',
                      color: '#6B7280',
                      fontSize: '14px'
                    }}>
                      No solves yet today
                    </div>
                  )}
                </div>

                {!isLoggedIn && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '16px',
                    background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0.8))',
                    backdropFilter: 'blur(4px)',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: '#6B7280',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}>
                    🔒 Sign in to see your position
                  </div>
                )}
              </div>
            </div>
          )}

          {!isLoggedIn && (
            <div style={{
              padding: '24px',
              background: '#F9FAFB',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px'
              }}>
                Create an account to:
              </div>
              <ul style={{
                margin: '0 0 20px 0',
                padding: '0 0 0 20px',
                color: '#4B5563',
                fontSize: '15px',
                lineHeight: '1.8'
              }}>
                <li><strong>See your exact rank today</strong></li>
                <li>Save your result to the leaderboard</li>
                <li>Share your solve</li>
                <li>Track your progress over time</li>
              </ul>
              <button
                onClick={handleCreateAccount}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.2s ease',
                  marginBottom: '12px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#E07B00';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FF8C00';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Create Account
              </button>
              <button
                onClick={handleLogIn}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'white',
                  color: '#1F2937',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#1F2937';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Log In
              </button>
            </div>
          )}

          {isLoggedIn && (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={handleShareResult}
                disabled={isGeneratingShare}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: 'white',
                  color: '#1F2937',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: isGeneratingShare ? 'wait' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isGeneratingShare ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isGeneratingShare) e.target.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                }}
              >
                {isGeneratingShare ? '⏳ Generating...' : '📤 Share Result'}
              </button>
              <button
                onClick={() => navigateTo('leaderboard')}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: 'white',
                  color: '#1F2937',
                  border: '2px solid #E5E7EB',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                }}
              >
                View Leaderboard
              </button>
            </div>
          )}

          <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '24px' }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {primaryCTAs.map((cta, index) => (
                <button
                  key={index}
                  onClick={cta.action}
                  style={{
                    flex: '1',
                    padding: '14px',
                    background: cta.isPrimary ? '#FF8C00' : 'rgba(255, 140, 0, 0.1)',
                    color: cta.isPrimary ? 'white' : '#FF8C00',
                    border: cta.isPrimary ? 'none' : '2px solid #FF8C00',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: cta.isPrimary ? '0 4px 12px rgba(255, 140, 0, 0.4)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (cta.isPrimary) {
                      e.target.style.background = '#E07B00';
                    } else {
                      e.target.style.background = 'rgba(255, 140, 0, 0.2)';
                    }
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    if (cta.isPrimary) {
                      e.target.style.background = '#FF8C00';
                    } else {
                      e.target.style.background = 'rgba(255, 140, 0, 0.1)';
                    }
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {cta.text}
                </button>
              ))}
            </div>

            {primaryCTAs[0]?.subtitle && (
              <div style={{
                textAlign: 'center',
                fontSize: '13px',
                color: '#6B7280',
                marginBottom: '16px'
              }}>
                {primaryCTAs[0].subtitle}
              </div>
            )}

            {allSolved && isLoggedIn && (
              <div style={{
                textAlign: 'center',
                fontSize: '14px',
                color: '#6B7280',
                marginTop: '12px'
              }}>
                Or{' '}
                <button
                  onClick={() => navigateTo('leaderboard')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563EB',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    padding: '0'
                  }}
                >
                  View Leaderboard
                </button>
                {' · '}
                <button
                  onClick={handleShareResult}
                  disabled={isGeneratingShare}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563EB',
                    cursor: isGeneratingShare ? 'wait' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    padding: '0',
                    opacity: isGeneratingShare ? 0.6 : 1
                  }}
                >
                  {isGeneratingShare ? 'Generating...' : 'Share Result'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigateTo('landing')}
            style={{
              background: 'transparent',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '10px 24px',
              fontSize: '15px',
              fontWeight: '600',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Share Result Card - Rendered off-screen for capture */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={shareCardRef} style={{
          width: '1080px',
          height: '1350px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0',
          margin: '0',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Enhanced Sudoku grid texture background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, transparent 1px, transparent 27px),
              repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0px, transparent 1px, transparent 27px),
              repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, transparent 3px, transparent 81px),
              repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0px, transparent 3px, transparent 81px)
            `,
            pointerEvents: 'none'
          }}></div>

          {/* Logo icon at top */}
          <div style={{
            position: 'absolute',
            top: '50px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Sudoku logo icon */}
            <svg width="48" height="48" viewBox="0 0 100 100" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
              <rect x="10" y="10" width="80" height="80" fill="none" stroke="white" strokeWidth="4"/>
              <line x1="10" y1="36.67" x2="90" y2="36.67" stroke="white" strokeWidth="2"/>
              <line x1="10" y1="63.33" x2="90" y2="63.33" stroke="white" strokeWidth="2"/>
              <line x1="36.67" y1="10" x2="36.67" y2="90" stroke="white" strokeWidth="2"/>
              <line x1="63.33" y1="10" x2="63.33" y2="90" stroke="white" strokeWidth="2"/>
              <rect x="63.33" y="10" width="26.67" height="26.67" fill="#2563EB"/>
              <rect x="10" y="63.33" width="26.67" height="26.67" fill="#FF8C00"/>
            </svg>
            <div style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'white',
              letterSpacing: '-1px',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              SudokuMatch
            </div>
          </div>

          {/* Orange accent line */}
          <div style={{
            position: 'absolute',
            top: '130px',
            width: '200px',
            height: '3px',
            background: '#FF8C00',
            borderRadius: '2px'
          }}></div>

          {/* Main white card - PERFECTLY CENTERED */}
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '70px 60px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center',
            width: '760px',
            margin: '0',
            position: 'relative'
          }}>
            {/* Username with optional founder badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '38px',
                fontWeight: '800',
                color: '#1F2937'
              }}>
                {userProfile?.username || 'Anonymous'}
              </div>
              {userProfile?.is_paid_founder && (
                <div style={{
                  width: '34px',
                  height: '38px',
                  display: 'inline-block'
                }}>
                  <svg width="34" height="38" viewBox="0 0 16 18">
                    <path
                      d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
                      fill="#B58B2E"
                      stroke="#B58B2E"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              )}
              {userProfile?.is_free_founder && !userProfile?.is_paid_founder && (
                <div style={{
                  width: '34px',
                  height: '38px',
                  display: 'inline-block'
                }}>
                  <svg width="34" height="38" viewBox="0 0 16 18">
                    <path
                      d="M8 0 L12 4 L16 4 L16 11 L8 18 L0 11 L0 4 L4 4 Z"
                      fill="none"
                      stroke="#2563EB"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* Difficulty badge */}
            <div style={{
              display: 'inline-block',
              padding: '14px 36px',
              background: solveData.difficulty === 'easy' ? '#D1FAE5' : solveData.difficulty === 'hard' ? '#DBEAFE' : '#FEE2E2',
              color: solveData.difficulty === 'easy' ? '#065F46' : solveData.difficulty === 'hard' ? '#1E40AF' : '#991B1B',
              borderRadius: '14px',
              fontSize: '26px',
              fontWeight: '700',
              marginBottom: '36px',
              textTransform: 'capitalize'
            }}>
              {solveData.difficulty} Puzzle
            </div>

            {/* Time */}
            <div style={{
              fontSize: '100px',
              fontWeight: '800',
              color: '#1F2937',
              fontFamily: 'monospace',
              marginBottom: '36px',
              lineHeight: '1',
              letterSpacing: '-2px'
            }}>
              {solveData.time === '00:00' && solveData.mode !== 'standard' ? solveData.actualTime : solveData.time}
            </div>

            {/* Current Rank/Percentile or Relaxed mode indicator */}
            {isStandardMode && isLoggedIn && shareRankData ? (
              <div>
                {/* Less than 10 solves: Show rank only */}
                {shareRankData.totalSolves < 10 ? (
                  <div>
                    <div style={{
                      fontSize: '34px',
                      fontWeight: '700',
                      color: '#2563EB',
                      marginBottom: '16px'
                    }}>
                      Current Rank #{shareRankData.currentRank}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#9CA3AF',
                      fontStyle: 'italic'
                    }}>
                      Final ranks lock at 00:00 UTC
                    </div>
                  </div>
                ) : (
                  /* 10+ solves: Show rank, total, and percentile */
                  <div>
                    <div style={{
                      fontSize: '34px',
                      fontWeight: '700',
                      color: '#2563EB',
                      marginBottom: '12px'
                    }}>
                      Current Rank #{shareRankData.currentRank} of {shareRankData.totalSolves}
                    </div>
                    {shareRankData.percentile !== null && (
                      <div style={{
                        fontSize: '24px',
                        color: '#6B7280',
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Faster than {shareRankData.percentile}%
                      </div>
                    )}
                    <div style={{
                      fontSize: '16px',
                      color: '#9CA3AF',
                      fontStyle: 'italic',
                      marginTop: '16px'
                    }}>
                      Final ranks lock at 00:00 UTC
                    </div>
                  </div>
                )}
              </div>
            ) : !isStandardMode ? (
              <div style={{
                fontSize: '28px',
                color: '#6B7280',
                fontWeight: '600'
              }}>
                Practice solve (not ranked)
              </div>
            ) : null}

            {/* Date */}
            <div style={{
              marginTop: '40px',
              paddingTop: '32px',
              borderTop: '2px solid #E5E7EB',
              fontSize: '20px',
              color: '#9CA3AF',
              fontWeight: '600',
              marginBottom: '24px'
            }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>

            {/* Small logo + wordmark at bottom of card */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              opacity: 0.7
            }}>
              <svg width="32" height="32" viewBox="0 0 100 100">
                <rect x="10" y="10" width="80" height="80" fill="none" stroke="#1F2937" strokeWidth="4"/>
                <line x1="10" y1="36.67" x2="90" y2="36.67" stroke="#1F2937" strokeWidth="2"/>
                <line x1="10" y1="63.33" x2="90" y2="63.33" stroke="#1F2937" strokeWidth="2"/>
                <line x1="36.67" y1="10" x2="36.67" y2="90" stroke="#1F2937" strokeWidth="2"/>
                <line x1="63.33" y1="10" x2="63.33" y2="90" stroke="#1F2937" strokeWidth="2"/>
                <rect x="63.33" y="10" width="26.67" height="26.67" fill="#2563EB"/>
                <rect x="10" y="63.33" width="26.67" height="26.67" fill="#FF8C00"/>
              </svg>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#6B7280',
                letterSpacing: '-0.5px'
              }}>
                SudokuMatch
              </div>
            </div>
          </div>

          {/* Footer with sudokumatch.com */}
          <div style={{
            position: 'absolute',
            bottom: '50px',
            fontSize: '28px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.9)',
            letterSpacing: '-0.5px',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            sudokumatch.com
          </div>
        </div>
      </div>

      {/* Share Error Message */}
      {shareError && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#DC2626',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1001
        }}>
          {shareError}
        </div>
      )}

      {/* Notification Modal */}
      {showNotifyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowNotifyModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
            }}
          >
            <h2 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}>
              Email Notifications
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              Choose when you'd like to be notified about puzzle updates
            </p>

            {/* Toggle Options */}
            <div style={{ marginBottom: '24px' }}>
              {/* Results Locked Toggle */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px'
                  }}>
                    When final results are locked in
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280'
                  }}>
                    Get notified when today's rankings are final
                  </div>
                </div>
                <div
                  onClick={() => setNotifyResultsLocked(!notifyResultsLocked)}
                  style={{
                    width: '50px',
                    height: '28px',
                    background: notifyResultsLocked ? '#10B981' : '#D1D5DB',
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: notifyResultsLocked ? '24px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}></div>
                </div>
              </div>

              {/* New Puzzle Toggle */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px'
                  }}>
                    When tomorrow's puzzle is live
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280'
                  }}>
                    Get notified at midnight when new puzzles drop
                  </div>
                </div>
                <div
                  onClick={() => setNotifyNewPuzzle(!notifyNewPuzzle)}
                  style={{
                    width: '50px',
                    height: '28px',
                    background: notifyNewPuzzle ? '#10B981' : '#D1D5DB',
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: notifyNewPuzzle ? '24px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}></div>
                </div>
              </div>

              {/* Daily Reminder Toggle */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0'
              }}>
                <div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '4px'
                  }}>
                    Make this a daily reminder
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6B7280'
                  }}>
                    Receive a daily email reminder to solve
                  </div>
                </div>
                <div
                  onClick={() => setNotifyDaily(!notifyDaily)}
                  style={{
                    width: '50px',
                    height: '28px',
                    background: notifyDaily ? '#10B981' : '#D1D5DB',
                    borderRadius: '14px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: notifyDaily ? '24px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}></div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowNotifyModal(false)}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: '#F3F4F6',
                  color: '#4B5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotifications}
                disabled={isSavingNotifications || notificationSaveSuccess || (!notifyResultsLocked && !notifyNewPuzzle && !notifyDaily)}
                style={{
                  flex: '1',
                  padding: '12px',
                  background: notificationSaveSuccess ? '#10B981' : (isSavingNotifications || (!notifyResultsLocked && !notifyNewPuzzle && !notifyDaily)) ? '#9CA3AF' : '#FF8C00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: (isSavingNotifications || notificationSaveSuccess || (!notifyResultsLocked && !notifyNewPuzzle && !notifyDaily)) ? 'not-allowed' : 'pointer',
                  boxShadow: notificationSaveSuccess ? '0 4px 12px rgba(16, 185, 129, 0.4)' : (isSavingNotifications || (!notifyResultsLocked && !notifyNewPuzzle && !notifyDaily)) ? 'none' : '0 4px 12px rgba(255, 140, 0, 0.4)',
                  transition: 'all 0.3s ease'
                }}
              >
                {notificationSaveSuccess ? '✓ Saved!' : isSavingNotifications ? 'Saving...' : 'Set up email notifications'}
              </button>
            </div>

            {/* Small Print */}
            <p style={{
              fontSize: '12px',
              color: '#9CA3AF',
              textAlign: 'center',
              margin: '12px 0 0 0',
              lineHeight: '1.4'
            }}>
              Preferences can be changed at any time in your Account
            </p>
          </div>
        </div>
      )}
      </div>

      <div style={{ marginTop: '40px' }}>
        <Footer navigateTo={navigateTo} />
      </div>
    </div>
  );
}

export default ResultScreen;
